import { Figura, type ItemLegenda } from './base'
import { inteiro, pct } from '../../lib/formato'

/**
 * Funil de comparecimento — e a correção de um erro de modelagem que
 * o pedido carrega.
 *
 * O pedido dizia "agendado → confirmado → compareceu → faltou". Isso
 * não é um funil: **faltou não é a quarta etapa, é a perda da
 * terceira.** Quem faltou não passou por "compareceu"; quem
 * compareceu nunca vai para "faltou". Desenhar os quatro como etapas
 * em sequência faz a última barra ser lida como "o fim do processo",
 * quando ela é o buraco do processo.
 *
 * O que existe de verdade são TRÊS etapas e a fuga entre elas:
 *
 *     agendado ─┬─ desmarcou (o paciente avisou)
 *               ├─ cancelou (a clínica desmarcou)
 *               └─ confirmado ─┬─ faltou (não avisou, não veio)
 *                              └─ compareceu
 *
 * E a distinção entre **desmarcou** e **faltou** é justamente o que o
 * `ui.tsx:168-176` chama de diferencial competitivo — e que a tela da
 * Agenda hoje joga fora, escrevendo "Cancelado" para os dois
 * (`Agenda.tsx:33-43`). O funil é o lugar onde essa distinção vira
 * dinheiro: desmarque com antecedência é horário que dá para revender
 * pela lista de espera; falta é horário perdido. São dois problemas
 * com dois remédios diferentes, e um número só esconde os dois.
 *
 * Por que não o trapézio clássico de funil: no trapézio o valor é
 * codificado na largura E na altura ao mesmo tempo, então a ÁREA
 * cresce ao quadrado e a queda parece maior do que é. Barras
 * alinhadas à esquerda, na mesma linha de base, comparam por
 * comprimento — a codificação que o olho lê melhor.
 *
 * Por que não Sankey: três etapas e três fugas não pagam o custo de
 * ler um diagrama de fluxo, e nenhuma biblioteca entra por isso.
 */

export type EtapaFunil = {
  chave: string
  rotulo: string
  n: number
  /** var(--cl-viz-etapa-N) */
  cor: string
  /** O que vazou ANTES de chegar nesta etapa. */
  perdas?: { rotulo: string; n: number; cor: string; acao?: string }[]
}

export function Funil({
  titulo, subtitulo, etapas, acao,
}: {
  titulo: string
  subtitulo?: string
  /** Da mais larga para a mais estreita. Máximo 4. */
  etapas: EtapaFunil[]
  acao?: React.ReactNode
}) {
  const base = etapas[0]?.n ?? 0
  const legenda: ItemLegenda[] = etapas.map((e) => ({ rotulo: e.rotulo, cor: e.cor }))

  return (
    <Figura
      titulo={titulo} subtitulo={subtitulo} legenda={legenda} acao={acao}
      tabela={{
        colunas: ['Etapa', 'Quantidade', '% do agendado', 'Perda até aqui'],
        linhas: etapas.flatMap((e) => [
          [e.rotulo, inteiro(e.n), pct(base > 0 ? (e.n / base) * 100 : 0),
           (e.perdas ?? []).map((p) => `${p.rotulo}: ${inteiro(p.n)}`).join(' · ') || '—'],
        ]),
      }}>
      <ol className="gfx-funil">
        {etapas.map((etapa, i) => {
          const anterior = etapas[i - 1]
          const largura = base > 0 ? (etapa.n / base) * 100 : 0
          return (
            <li key={etapa.chave} className="gfx-funil__etapa">
              {/* A perda vem ANTES da etapa que ela alimenta: lê-se
                  "de 120 agendados, 14 desmarcaram, sobraram 106". */}
              {etapa.perdas && etapa.perdas.length > 0 && (
                <ul className="gfx-funil__perdas">
                  {etapa.perdas.map((p) => (
                    <li key={p.rotulo}>
                      <span className="gfx-funil__seta" aria-hidden="true">↳</span>
                      <span className="gfx-funil__chave" aria-hidden="true"
                            style={{ background: p.cor }} />
                      <strong>−{inteiro(p.n)}</strong> {p.rotulo}
                      {anterior && anterior.n > 0 && (
                        <span className="gfx-funil__pct">
                          {' '}({pct((p.n / anterior.n) * 100, 1)} de {anterior.rotulo.toLowerCase()})
                        </span>
                      )}
                      {p.acao && <span className="gfx-funil__acao">{p.acao}</span>}
                    </li>
                  ))}
                </ul>
              )}

              <div className="gfx-funil__linha">
                <span className="gfx-funil__rotulo">{etapa.rotulo}</span>
                <span className="gfx-funil__trilho">
                  <span className="gfx-funil__barra"
                        style={{ width: `${largura}%`, background: etapa.cor }} />
                </span>
                <span className="gfx-funil__n">
                  {inteiro(etapa.n)}
                  <span className="gfx-funil__pct">
                    {' '}{pct(base > 0 ? (etapa.n / base) * 100 : 0)}
                  </span>
                </span>
              </div>
            </li>
          )
        })}
      </ol>
    </Figura>
  )
}
